import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "@/app/store";
import { APIError, APIResponse } from "@/types/custom-http";
import { workspacesApi } from "./workspacesApi";
import { Workspace, WorkspaceInfo, CreateWorkspace, UpdateWorkspace } from "./workspaces.interface";

export const fetchWorkspaces = createAsyncThunk<
  APIResponse<Workspace[]>,
  void,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("workspaces/fetchWorkspaces", async (_, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(workspacesApi.endpoints.getWorkspaces.initiate()).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al obtener Workspaces",
      details: data.details,
    });
  }
});

export const fetchWorkspacesInfo = createAsyncThunk<
  APIResponse<WorkspaceInfo[]>,
  void,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("workspaces/fetchWorkspacesInfo", async (_, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(workspacesApi.endpoints.getWorkspacesInfo.initiate()).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al obtener información de Workspaces",
      details: data.details,
    });
  }
});

export const fetchWorkspaceById = createAsyncThunk<
  APIResponse<Workspace>,
  number,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("workspaces/fetchWorkspaceById", async (id, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(workspacesApi.endpoints.getWorkspaceById.initiate(id)).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al obtener Workspace",
      details: data.details,
    });
  }
});

export const searchWorkspaces = createAsyncThunk<
  APIResponse<Workspace[]>,
  string,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("workspaces/searchWorkspaces", async (search, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(workspacesApi.endpoints.searchWorkspaces.initiate(search)).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al buscar Workspaces",
      details: data.details,
    });
  }
});

export const createWorkspace = createAsyncThunk<
  APIResponse<Workspace>,
  CreateWorkspace,
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("workspaces/createWorkspace", async (data, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(workspacesApi.endpoints.createWorkspace.initiate(data)).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al crear Workspace",
      details: data.details,
    });
  }
});

export const updateWorkspace = createAsyncThunk<
  APIResponse<Workspace>,
  { id: number; changes: UpdateWorkspace },
  {
    state: RootState;
    rejectValue: { status: number; message: string; details?: Record<string, string[]> };
  }
>("workspaces/updateWorkspace", async ({ id, changes }, { dispatch, rejectWithValue }) => {
  try {
    const result = await dispatch(workspacesApi.endpoints.updateWorkspace.initiate({ id, changes })).unwrap();
    return result;
  } catch (error) {
    const apiError = error as APIError;
    const { status, data } = apiError;
    return rejectWithValue({
      status,
      message: data?.message || "Error al actualizar Workspace",
      details: data.details,
    });
  }
});
